import React, { useState } from "react";
import HeroSection from "../../../Components/Common/HeroSection";
import useSWR from "swr";
import {
  orderUrls,
  StaticPageApiUrls,
  UserApiUrls,
} from "../../../Api/Auth/ApiUrls";
import {
  customFetcher,
  fetcher,
  sendPostRequestAuth,
} from "../../../Api/swrConfig";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../Context/AuthContext";
import Button from "../../../Components/Common/Button";
import useSWRMutation from "swr/mutation";
import { toast } from "react-toastify";
import Modal from "../../../Components/Common/Modal";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Loader } from "../../../Components/Common/Loader";
import { publicUrls, studentUrls } from "../../../Api/Auth/AppUrls";
import { motion } from "framer-motion";
import Input from "../../../Components/Common/Input";
import useToastError from "../../../hooks/useErrorToast";

const stripePromise = loadStripe(process.env.REACT_APP_Publishable_Key);
const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#333",
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: { color: "#fa755a" },
  },
};
const formatDuration = (timeString) => {
  if (!timeString) return "";

  // Ensure the time is in "HH:MM" format
  let [hours, minutes] = timeString.split(":").map((num) => parseInt(num, 10));

  hours = isNaN(hours) ? 0 : hours;
  minutes = isNaN(minutes) ? 0 : minutes;

  if (hours === 0 && minutes === 0) return "0 mins"; // Handle case where both are 0
  if (hours === 0) return `${minutes} min${minutes > 1 ? "s" : ""}`;
  if (minutes === 0) return `${hours} hr${hours > 1 ? "s" : ""}`;

  return `${hours} hr${hours > 1 ? "s" : ""} ${minutes} min${minutes > 1 ? "s" : ""
    }`;
};

const PaymentForm = ({ clientSecret, onClose, mutate, item }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [saveCard, setSaveCard] = useState(false);

  const { data } = useSWR(UserApiUrls.savedCards, fetcher);
  const savedCards = data?.cards || [];

  const { trigger } = useSWRMutation(orderUrls.payment, sendPostRequestAuth, {
    onSuccess: (data) => {
      mutate();
      toast.info(
        `You can access your one to one tutoring from your student dashboard too`
      );
    },
    onError: (error) => {
      toast.error(error.message || "Payment failed. Please try again.");
    },
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);

    // Get the card number element explicitly
    try {
      let paymentMethodId;

      if (selectedCard) {
        paymentMethodId = selectedCard; // Use selected saved card
      } else {
        const cardElement = elements.getElement(CardNumberElement);
        if (!cardElement) {
          toast.error("Card details not found. Please try again.");
          setIsProcessing(false);
          return;
        }

        // Create a payment method using the card details
        const { paymentMethod, error: pmError } =
          await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
          });

        if (pmError) {
          toast.error(pmError.message || "Failed to create payment method.");
          setIsProcessing(false);
          return;
        }
        paymentMethodId = paymentMethod.id;
      }
      // Confirm payment
      const { paymentIntent, error: confirmError } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethodId,
          setup_future_usage: "off_session",
        });

      if (confirmError) {
        toast.error(
          confirmError.message || "Payment failed. Please try again."
        );
      } else if (paymentIntent?.status === "succeeded") {
        toast.success("Payment successful!");

        // Send request to backend to update order status
        await trigger({
          paymentIntentId: paymentIntent.id,
          itemName: `${item?.displayTitle ||
            item?.title ||
            "One to One Tutoring"
            } (Mechanical)`,
          saveCard: saveCard,
        });

        onClose();
      }
    } catch (err) {
      const dupCardError =
        err?.response?.data?.message ===
        "The payment method you provided is already attached to your saved cards list. Please use your saved card instead.";
      if (dupCardError) {
        return toast.error(err?.response?.data?.message);
      }
      console.error("Payment error:", err);
      toast.error("An unexpected error occurred.");
    }

    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded-lg p-3">
      {savedCards.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Saved Cards
          </label>
          <select
            className="p-2 border rounded-md w-full"
            value={selectedCard || ""}
            onChange={(e) => setSelectedCard(e.target.value || null)}
          >
            <option value="">Use a new card</option>
            {savedCards.map((card) => (
              <option key={card.id} value={card.id}>
                {`**** **** **** ${card.card.last4
                  } (${card.card.brand.toUpperCase()})`}
              </option>
            ))}
          </select>
        </div>
      )}

      {!selectedCard && (
        <>
          <label className="block text-sm font-medium text-gray-700">
            Card Number
          </label>
          <CardNumberElement
            className="p-2 border rounded-md"
            options={cardElementOptions}
          />

          <div className="flex space-x-4">
            <div className="w-1/2">
              <label className="block text-sm font-medium text-gray-700">
                Expiry Date
              </label>
              <CardExpiryElement
                className="p-2 border rounded-md w-full"
                options={cardElementOptions}
              />
            </div>

            <div className="w-1/2">
              <label className="block text-sm font-medium text-gray-700">
                CVC
              </label>
              <CardCvcElement
                className="p-2 border rounded-md w-full"
                options={cardElementOptions}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="saveCard"
              checked={saveCard}
              onChange={() => setSaveCard(!saveCard)}
              className="h-4 w-4"
            />
            <label htmlFor="saveCard" className="text-sm text-gray-700">
              Save this card for future payments
            </label>
          </div>
        </>
      )}

      <Button type="primary" className="mt-4 w-full" disabled={isProcessing}>
        {isProcessing ? "Processing..." : "Pay Now"}
      </Button>
    </form>
  );
};

const TutoringMechanical = () => {
  const location = useLocation();
  const { showErrorToast } = useToastError();
  const { token, userData } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [selected, setSelected] = useState(null);
  const [paymentConfirmScreen, setPaymentConfirmScreen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState("");
  const [discountedPrice, setDiscountedPrice] = useState(null);
  const slug = location.pathname.replace("/mechanical/", "mechanical-");
  const navigate = useNavigate();

  const { data, isLoading, mutate } = useSWR(
    slug ? StaticPageApiUrls.info(slug) : null,
    (url) => customFetcher(url, token)
  );

  const { trigger: promoTrigger } = useSWRMutation(
    orderUrls.promo,
    sendPostRequestAuth,
    {
      onSuccess: (data) => {
        const calculatedPrice = selected.price - (selected.price * data?.discount) / 100;
        setDiscountedPrice(parseFloat(calculatedPrice.toFixed(2)));
        setAppliedPromo(promoCode);
        toast.success(`Promo Code applied successfully`);
      },
      onError: (error) => {
        console.log(error);
        showErrorToast(error || "Invalid promo code. Please try again.");
      },
    }
  );

  const onBuyClick = (item) => {
    setSelected(item);
    setPaymentConfirmScreen(true);
    setIsModalOpen(true);
  };

  const onBuy = async (item) => {
    const payload = {
      userId: userData?._id,
      packageId: item._id,
      promo: discountedPrice ? appliedPromo : undefined,
      slug: slug,
      customer: {
        id: userData?._id,
        name: userData?.name,
        email: userData?.email,
      },
    };
    await purchaseItem(payload);
  };

  const handleApplyPromo = async (e) => {
    e.preventDefault();
    if (!promoCode) {
      return toast.error("Enter Promo Code");
    }
    await promoTrigger({
      promoCode: promoCode,
      slug: slug,
      price: selected.price,
    });
  };

  const { trigger: purchaseItem, isMutating } = useSWRMutation(
    orderUrls.create,
    sendPostRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Processing payment...");
        setClientSecret(data.clientSecret);
        setPaymentConfirmScreen(false);
        // setIsModalOpen(true);
      },
      onError: (error) => {
        toast.error(error.message || "Payment failed. Please try again.");
      },
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 1, y: -40 }} // Start above the view
      whileInView={{ opacity: 1, y: 0 }} // End at normal position
      transition={{ duration: 1, ease: "easeOut" }} // Longer transition for more emphasis
    >
      <HeroSection
        title={
          data?.data?.displayTitle ||
          data?.data?.title ||
          "Our Mechanical Tutoring"
        }
        backgroundImage="https://storage.googleapis.com/road-to-fe/uploads/1741417535659-07d447cd-82a2-4b1b-8c08-9fffeaaefa18-colleagues-doing-team-work-project_23-2149361607.avif"
        subtitle={data?.data?.headerDescription || "One Platform Many Options To Explore"}
      />
      <div className="gap-3 container mx-auto sm:mt-12 mt-4 flex justify-center md:justify-between items-start flex-wrap md:flex-nowrap px-2">
        <div
          className="max-w-5xl w-full"
          dangerouslySetInnerHTML={{ __html: data?.data?.bodyDescription }}
        />
        <div>
          <img
            src={data?.data?.image}
            alt="Tutoring"
            className="w-full h-72 object-cover rounded-md"
          />
        </div>
      </div>
      <h2 className="text-3xl font-semibold text-primary text-center mt-4">
        {`Try our ${data?.data?.displayTitle || data?.data?.title || "One to One Tutoring"
          }!`}
      </h2>
      <div className="flex justify-center items-center">
        {/* {userData ? ( */}
        <div className="container mx-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-6 p-6">
          {data?.data?.packages?.length
            ? data.data.packages.map((item) => (
              <div
                key={item._id}
                className="bg-white shadow-lg hover:shadow-xl rounded-lg p-4 transition-all duration-300"
              >
                <button className="block w-full flex justify-center">
                  {!item?.image ? (
                    <img
                      src="/fileDownload.png"
                      alt={item.title}
                      className="w-36 h-36 object-cover transform group-hover:scale-110 transition-transform duration-300"
                    />
                  ) : (
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-36 h-36 object-cover rounded-md"
                    />
                  )}
                </button>
                <div className="flex flex-col items-center mt-3 gap-1">
                  <h2 className="text-sm font-semibold">{item.title}</h2>
                  <p className="text-sm font-semibold text-primary">
                    ${item.price}
                  </p>

                  {/* Duration with a subtle but clear format */}
                  {item?.time && (
                    <p className="text-xs text-gray-500 italic">
                      {formatDuration(item.time)}
                    </p>
                  )}

                  {item?.purchased ? (
                    <Link to={studentUrls.studentTutoringMechanical}>
                      <Button>Dashboard</Button>
                    </Link>
                  ) : (
                    <Button
                      onClick={() =>
                        !userData ? navigate("/login") : onBuyClick(item)
                      }
                      disabled={isMutating}
                    >
                      {item?.actionButton || "Buy Now"}
                    </Button>
                  )}
                </div>
              </div>
            ))
            : null}
        </div>
        {/* ) : (
          <Link to="/login" className="mt-6">
            <Button type="primary" className="w-full">
              Login
            </Button>
          </Link>
        )} */}
      </div>
      <Modal
        isOpen={isModalOpen}
        title={"Payment"}
        onCancel={() => {
          setIsModalOpen(false);
          setSelected(null);
          setPaymentConfirmScreen(false);
          setPromoCode("");
          setDiscountedPrice(null);
          setAppliedPromo("");
        }}
        isLoading={isMutating}
        formWidth="sm:max-w-[50%]"
        footer={false}
      >
        {paymentConfirmScreen && selected && (
          <div className="space-y-4">
            {/* Price Display */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:w-1/2">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 font-medium">
                    Total Price
                  </span>
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {discountedPrice ? (
                    <>
                      <span className="line-through text-gray-400 mr-2">
                        ${selected.price}
                      </span>
                      <span className="text-green-600">${discountedPrice}</span>
                    </>
                  ) : (
                    <span>${selected.price}</span>
                  )}
                </div>
              </div>
              {discountedPrice && (
                <p className="text-xs text-gray-500">
                  A promo code has been applied. You saved{" "}
                  <span className="font-semibold text-green-600">
                    ${parseFloat((selected.price - discountedPrice).toFixed(2))}
                  </span>
                  !
                </p>
              )}
            </div>

            {/* Promo Code Input with Apply Button */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Promo Code
              </label>
              <div className="flex flex-wrap gap-2">
                <Input
                  type="text"
                  value={promoCode}
                  onChange={(_, value) => setPromoCode(value)}
                  placeholder="Enter promo code"
                  className="flex-1 p-2"
                />
                <Button
                  type="button"
                  size="small"
                  className="bg-primary text-white text-center my-2"
                  onClick={(e) => {
                    handleApplyPromo(e);
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              By purchasing, you agree to our{" "}
              <a
                href={publicUrls.TermsOfServices}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-primary font-medium"
              >
                terms & conditions
              </a>
              .
            </p>
            <Button
              type="button"
              className="bg-primary text-white text-center"
              onClick={() => {
                onBuy(selected);
              }}
            >
              Purchase
            </Button>
          </div>
        )}

        {clientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentForm
              onClose={() => {
                setIsModalOpen(false);
                setClientSecret("");
              }}
              clientSecret={clientSecret}
              mutate={mutate}
              item={selected}
            />
          </Elements>
        )}
      </Modal>
    </motion.div>
  );
};

export default TutoringMechanical;
